import { cn } from '@coas/ui/lib';
import type { RouterOutputs } from 'trpc/shared';
import { MAX_PRODUCT_PRICE_CART_COUNT } from '~/entities/Cart';
import { useAddToCart, useRemoveFromCart } from '~/entities/Cart';

type CounterProps = {
    cartItem: RouterOutputs['cart']['getAll']['items'][number];
    interactive?: boolean;
};

export const Counter = ({ cartItem, interactive = false }: CounterProps) => {
    const { productPriceId } = cartItem;

    const addMutation = useAddToCart();
    const removeMutation = useRemoveFromCart();

    const isLoading = addMutation.isPending || removeMutation.isPending;
    const count = cartItem.count;
    const isMax = count >= MAX_PRODUCT_PRICE_CART_COUNT;

    const handleDecrement = () => {
        if (isLoading) return;
        removeMutation.mutate({ productPriceId });
    };

    const handleIncrement = () => {
        if (isLoading || isMax) return;
        addMutation.mutate({ productPriceId });
    };

    return (
        <div className={cn('inline-flex items-center self-start font-futura font-600 leading-none gap-2')}>
            {interactive && (
                <button
                    type='button'
                    aria-busy={isLoading}
                    className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200',
                        isLoading && 'animate-pulse',
                    )}
                    onClick={handleDecrement}
                    disabled={isLoading}
                >
                    −
                </button>
            )}

            <span
                className={cn(
                    'inline-flex h-8',
                    interactive
                        ? 'w-16 justify-center items-center text-center'
                        : 'justify-start items-start text-left',
                )}
            >
                {count}
            </span>

            {interactive && (
                <button
                    type='button'
                    aria-busy={isLoading}
                    className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200',
                        isMax && 'opacity-50',
                        isLoading && 'animate-pulse',
                    )}
                    onClick={handleIncrement}
                    disabled={isLoading || isMax}
                >
                    +
                </button>
            )}
        </div>
    );
};
